// src/stores/invoiceStore.js
import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import svc from "@/services/finance/invoice.service";
import * as Toast from "@/utils/toast"; // ⬅️ important: import as *
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const invoiceRangeStatsCache = createScopedCache("finance:invoice:stats-range");
const INVOICE_RANGE_STATS_TTL = 2 * 60 * 1000;

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useInvoiceStore = defineStore("invoice", () => {
  const items = ref([]);
  const current = ref(null);

  const stats = reactive({
    total: 0,
    outstanding: 0,
    draft: 0,
    overdue: 0,
    total_pct: 0,
    outstanding_pct: 0,
    draft_pct: 0,
    overdue_pct: 0,
  });

  const loading = ref(false);
  const loadingForCreate = ref(false);
  const loadingForUpdate = ref(false);
  const loadingFordelete = ref(false);
  const loadingForReceivePayment = ref(false);
  const meta = ref({ total: 0, per_page: 25, current_page: 1 });

  const filters = reactive({
    status: "",
    compliance_status: "",
    document_type: "",
    from: null,
    to: null,
    q: "",
  });

  const isEmpty = computed(() => !items.value.length && !loading.value);

  async function fetchAll(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      items.value = [];
      return { data: { data: [], total: 0, per_page: 25, current_page: 1 } };
    }

    loading.value = true;
    try {
      const res = await svc.getAll({
        page: params.page ?? meta.value.current_page ?? 1,
        status: (params.status ?? filters.status) || undefined,
        compliance_status:
          (params.compliance_status ?? filters.compliance_status) || undefined,
        document_type:
          (params.document_type ?? filters.document_type) || undefined,
        from: (params.from ?? filters.from) || undefined,
        to: (params.to ?? filters.to) || undefined,
        q: (params.q ?? filters.q) || undefined,
        per_page: params.per_page ?? meta.value.per_page ?? 25,
      });

      items.value = res.data.data || res.data;
      meta.value = {
        total: res.data.total,
        per_page: res.data.per_page,
        current_page: res.data.current_page,
      };
    } catch (e) {
      notifyError("Failed to load invoices");
    } finally {
      loading.value = false;
    }
  }

  async function fetchStats(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      return;
    }

    try {
      const res = await svc.stats({
        status: (extra.status ?? filters.status) || undefined,
        compliance_status:
          (extra.compliance_status ?? filters.compliance_status) || undefined,
        document_type:
          (extra.document_type ?? filters.document_type) || undefined,
        from: (extra.from ?? filters.from) || undefined,
        to: (extra.to ?? filters.to) || undefined,
        q: (extra.q ?? filters.q) || undefined,
      });
      Object.assign(stats, res.data);
    } catch {
      // silent
    }
  }

  async function get(id) {
    const res = await svc.get(id);
    current.value = res.data;
    return res.data;
  }

  async function create(payload) {
    const res = await svc.create(payload);
    notifySuccess("Invoice created");
    // refresh list + stats
    await fetchAll({ page: 1 });
    await fetchStats();
    return res.data;
  }

  async function update(id, payload) {
    const res = await svc.update(id, payload);
    notifySuccess("Invoice updated");
    current.value = res.data;
    await fetchAll({ page: meta.value.current_page || 1 });
    await fetchStats();
    return res.data;
  }

  async function destroy(id) {
    loadingFordelete.value = true;
    try {
      await svc.delete(id);
      notifySuccess("Invoice deleted");
      items.value = items.value.filter((i) => i.id !== id);
      await fetchStats();
    } catch (error) {
      notifyError("Failed to delete invoice");
    } finally {
      loadingFordelete.value = false;
    }
  }

  async function fetchNextNumber() {
    const res = await svc.nextNumber();
    return res.data.invoice_no;
  }

  async function receivePayment(id, payload) {
    loadingForReceivePayment.value = true;

    try {
      const res = await svc.receivePayment(id, payload);
      notifySuccess("Payment recorded");

      const updated = res.data;

      // update current invoice
      if (current.value && current.value.id === updated.id) {
        current.value = updated;
      }

      // update list
      const idx = items.value.findIndex((i) => i.id === updated.id);
      if (idx !== -1) {
        items.value.splice(idx, 1, updated);
      }

      try {
        await fetchStats();
      } catch (e) {
        console.warn("Payment recorded but failed to refresh stats", e);
      }

      return updated;
    } catch (error) {
      notifyError("Receive Payment failed");
    } finally {
      loadingForReceivePayment.value = false;
    }
  }


   async function statsForRange(extra = {}) {
    try {
      return await invoiceRangeStatsCache.get(extra, {
        ttlMs: INVOICE_RANGE_STATS_TTL,
        force: extra.force === true,
        loader: async () => {
          const res = await svc.stats(extra);
          return res.data || {};
        },
      });
    } catch (e) {
      console.error("Failed to fetch invoice stats for range", e);
      return {
        total: 0,
        outstanding: 0,
        draft: 0,
        overdue: 0,
        total_pct: 0,
        outstanding_pct: 0,
        draft_pct: 0,
        overdue_pct: 0,
        total_invoices: 0,
        paid_invoices: 0,
        overdue_invoices: 0,
        partially_paid_invoices: 0,
        unpaid_invoices: 0,
        revenue: 0,
      };
    }
  }

  async function ensureStatsForRange(extra = {}) {
    return statsForRange({ ...extra, force: false });
  }


 // NEW: time-series data for chart
  async function seriesForRange(extra = {}) {
    try {
      const params = {
        status: (extra.status ?? filters.status) || undefined,
        compliance_status:
          (extra.compliance_status ?? filters.compliance_status) || undefined,
        document_type:
          (extra.document_type ?? filters.document_type) || undefined,
        from: (extra.from ?? filters.from) || undefined,
        to: (extra.to ?? filters.to) || undefined,
        q: (extra.q ?? filters.q) || undefined,
      };

      const res = await svc.series(params);
      return res.data || [];
    } catch (e) {
      console.error("Failed to fetch invoice series for range", e);
      return [];
    }
  }

  // NEW: Excel export
  async function exportPdf(extra = {}) {
    loading.value = true;
    try {
      const params = {
        status: (extra.status ?? filters.status) || undefined,
        compliance_status:
          (extra.compliance_status ?? filters.compliance_status) || undefined,
        document_type:
          (extra.document_type ?? filters.document_type) || undefined,
        from: (extra.from ?? filters.from) || undefined,
        to: (extra.to ?? filters.to) || undefined,
        q: (extra.q ?? filters.q) || undefined,
      };

      const res = await svc.exportPdf(params);
      return res.data; // blob
    } catch (e) {
      notifyError("Failed to export invoice report PDF");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function exportExcel(extra = {}) {
    loading.value = true;
    try {
      const params = {
        status: (extra.status ?? filters.status) || undefined,
        compliance_status:
          (extra.compliance_status ?? filters.compliance_status) || undefined,
        document_type:
          (extra.document_type ?? filters.document_type) || undefined,
        from: (extra.from ?? filters.from) || undefined,
        to: (extra.to ?? filters.to) || undefined,
        q: (extra.q ?? filters.q) || undefined,
      };

      const res = await svc.exportExcel(params);
      return res.data; // blob
    } catch (e) {
      notifyError("Failed to export invoice report Excel");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    current,
    stats,
    meta,
    filters,
    loading,
    loadingFordelete,
    loadingForReceivePayment,
    isEmpty,
    fetchAll,
    fetchStats,
    fetchNextNumber,
    get,
    create,
    update,
    destroy,
    receivePayment,
    statsForRange,
    ensureStatsForRange,
    exportPdf,
    exportExcel,
    seriesForRange,
  };
});
